import type { ColumnDef, RowSelectionState, Table } from '@tanstack/react-table'
import { Trash2Icon } from 'lucide-react'
import { type RefObject, useEffect } from 'react'

import { Button, DataTable, type DataTableColumns } from '@/components/ui'
import {
  DeleteDocumentsDialog,
  type Document,
  ProcessDocumentsButton,
  useDocuments,
  useLoadingDocuments,
} from '@/modules/documents'
import {
  isDocumentProcessing,
  isDocumentToStart,
} from '@/modules/documents/status'
import { useDialogActions, useSubmittedDialog } from '@/stores/dialog'
import { cn } from '@/utils'

interface DocumentsDataTableProps {
  onTableReady?: (table: Table<{ id: string | number }>) => void
  columns: ColumnDef<Document, any>[]
  table: RefObject<Table<any> | null>
}

export const DocumentsDataTable = ({
  columns,
  table,
  onTableReady,
}: DocumentsDataTableProps) => {
  const { openDialog } = useDialogActions()
  const submittedDialog = useSubmittedDialog()

  const documents = useDocuments()
  const isLoadingDocuments = useLoadingDocuments()

  const getDocumentsFromSelection = (
    selection: RowSelectionState,
  ): Document[] => documents.filter((document) => selection[document.id])

  useEffect(() => {
    if (submittedDialog && table.current) {
      table.current.resetRowSelection()
    }
  }, [submittedDialog, table])

  return (
    <div className={cn('flex-1 flex flex-col overflow-auto')}>
      <DataTable
        columns={columns as DataTableColumns}
        data={documents}
        height="h-auto"
        onTableReady={onTableReady}
        emptyMessage="No documents found"
        isLoading={isLoadingDocuments}
        scrollbarClassName="overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent"
        bulkActions={(selection) => {
          const documents = getDocumentsFromSelection(selection)
          const hasProcessingDocuments = !!documents.find(isDocumentProcessing)

          return (
            <>
              <ProcessDocumentsButton
                table={table.current}
                documentsToProcess={documents}
                size="sm"
                theme="outline"
                className="h-8 gap-1.5 text-xs font-medium"
                disabled={documents.some(
                  (document) => !isDocumentToStart(document),
                )}
              >
                Process
              </ProcessDocumentsButton>
              <Button
                theme="outline"
                size="sm"
                onClick={() => openDialog(DeleteDocumentsDialog.id, documents)}
                className="h-8 gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
                disabled={hasProcessingDocuments}
              >
                <Trash2Icon className="size-3.5" />
                Delete
              </Button>
            </>
          )
        }}
      />
    </div>
  )
}
